import React, { useState, useRef, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { Camera, Upload, X, Video } from 'lucide-react';

const REFERRAL_OPTIONS = ['지인소개', '인스타그램', '네이버', '간판', '유튜브', '기타'];
const PURPOSE_OPTIONS = ['다이어트', '근력증가', '체력증진', '재활운동', '체형교정', '기타'];

// 웹캠 캡처 컴포넌트
const WebcamCapture = ({ onCapture, onClose }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState('');

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: 640, height: 480 } });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        setStreaming(true);
      }
    } catch (e) {
      setError('카메라 접근 권한이 필요합니다. 브라우저 설정에서 허용해 주세요.');
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(t => t.stop());
    }
    setStreaming(false);
  }, []);

  const capture = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    canvasRef.current.width = videoRef.current.videoWidth;
    canvasRef.current.height = videoRef.current.videoHeight;
    ctx.drawImage(videoRef.current, 0, 0);
    const dataUrl = canvasRef.current.toDataURL('image/jpeg', 0.8);
    stopCamera();
    onCapture(dataUrl);
    onClose();
  }, [stopCamera, onCapture, onClose]);

  React.useEffect(() => {
    startCamera();
    return () => stopCamera();
  }, [startCamera, stopCamera]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80">
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 w-full max-w-sm mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900 dark:text-white">사진 촬영</h3>
          <button onClick={() => { stopCamera(); onClose(); }} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        {error ? (
          <div className="text-red-500 text-sm text-center py-8">{error}</div>
        ) : (
          <div className="relative bg-black rounded-xl overflow-hidden" style={{ aspectRatio: '4/3' }}>
            <video ref={videoRef} className="w-full h-full object-cover" muted playsInline />
            {/* 얼굴 가이드 오버레이 */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-40 h-48 border-2 border-white/60 rounded-full" style={{ boxShadow: '0 0 0 9999px rgba(0,0,0,0.4)' }} />
            </div>
          </div>
        )}
        <canvas ref={canvasRef} className="hidden" />
        <div className="flex gap-3 mt-4">
          <button onClick={() => { stopCamera(); onClose(); }} className="btn-secondary flex-1">취소</button>
          <button onClick={capture} disabled={!streaming} className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50">
            <Camera className="w-4 h-4" /> 촬영
          </button>
        </div>
      </div>
    </div>
  );
};

const MemberForm = ({ member, onClose }) => {
  const { addMember, updateMember } = useApp();
  const fileInputRef = useRef(null);
  const [showCamera, setShowCamera] = useState(false);

  const [form, setForm] = useState({
    name: member?.name || '',
    gender: member?.gender || 'male',
    birthDate: member?.birthDate || '',
    phone: member?.phone || '',
    email: member?.email || '',
    address: member?.address || '',
    photo: member?.photo || null,
    purpose: member?.purpose || '',
    referral: member?.referral || '',
    memo: member?.memo || '',
    status: member?.status || 'active',
  });

  const [errors, setErrors] = useState({});

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const hasKorean = (str) => /[ㄱ-ㅎㅏ-ㅣ가-힣]/.test(str);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = '이름을 입력하세요.';
    if (!form.phone.trim()) e.phone = '전화번호를 입력하세요.';
    if (form.email && !emailRegex.test(form.email)) e.email = '유효한 이메일 주소를 입력하세요.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handlePhotoFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => set('photo', reader.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (member) updateMember(member.id, form);
    else addMember(form);
    onClose();
  };

  return (
    <>
      {showCamera && (
        <WebcamCapture onCapture={(img) => set('photo', img)} onClose={() => setShowCamera(false)} />
      )}
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* 사진 영역 */}
        <div className="flex flex-col items-center gap-3">
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-gray-100 dark:bg-gray-700 border-2 border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center overflow-hidden">
              {form.photo ? (
                <img src={form.photo} alt="프로필" className="w-full h-full object-cover" />
              ) : (
                <Camera className="w-8 h-8 text-gray-400" />
              )}
            </div>
            {form.photo && (
              <button
                type="button"
                onClick={() => set('photo', null)}
                className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowCamera(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 text-xs font-medium rounded-lg hover:bg-primary-100 transition-colors border border-primary-200 dark:border-primary-800"
            >
              <Video className="w-3.5 h-3.5" /> 카메라 촬영
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs font-medium rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              <Upload className="w-3.5 h-3.5" /> 파일 선택
            </button>
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoFile} />
        </div>

        {/* 기본 정보 */}
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 sm:col-span-1">
            <label className="label">이름 <span className="text-red-500">*</span></label>
            <input type="text" value={form.name} onChange={e => set('name', e.target.value)} className={`input ${errors.name ? 'border-red-400 focus:ring-red-400' : ''}`} placeholder="홍길동" />
            {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="label">성별</label>
            <div className="flex gap-2">
              {[{ v: 'male', l: '남성' }, { v: 'female', l: '여성' }].map(({ v, l }) => (
                <button key={v} type="button" onClick={() => set('gender', v)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${form.gender === v ? 'bg-primary-600 text-white border-primary-600' : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:border-primary-400'}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="label">생년월일</label>
            <input type="date" value={form.birthDate} onChange={e => set('birthDate', e.target.value)} className="input" />
          </div>

          <div>
            <label className="label">전화번호 <span className="text-red-500">*</span></label>
            <input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} className={`input ${errors.phone ? 'border-red-400 focus:ring-red-400' : ''}`} placeholder="010-0000-0000" />
            {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
          </div>

          <div className="col-span-2">
            <label className="label">이메일 <span className="text-xs text-gray-400 font-normal">(회원 앱 로그인용)</span></label>
            <input
              type="text"
              value={form.email}
              onChange={e => set('email', e.target.value)}
              className={`input ${hasKorean(form.email) || errors.email ? 'border-red-400 focus:ring-red-400' : ''}`}
              placeholder="member@example.com"
            />
            {hasKorean(form.email) && (
              <p className="text-xs text-red-500 mt-1">이메일은 영문, 숫자, 특수문자만 가능합니다</p>
            )}
            {errors.email && !hasKorean(form.email) && (
              <p className="text-xs text-red-500 mt-1">{errors.email}</p>
            )}
          </div>

          <div className="col-span-2">
            <label className="label">주소</label>
            <input type="text" value={form.address} onChange={e => set('address', e.target.value)} className="input" placeholder="서울시 강남구..." />
          </div>
        </div>

        {/* 추가 정보 */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">운동 목적</label>
            <select value={form.purpose} onChange={e => set('purpose', e.target.value)} className="input">
              <option value="">선택하세요</option>
              {PURPOSE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className="label">알게된 경로</label>
            <select value={form.referral} onChange={e => set('referral', e.target.value)} className="input">
              <option value="">선택하세요</option>
              {REFERRAL_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className="label">회원 상태</label>
            <select value={form.status} onChange={e => set('status', e.target.value)} className="input">
              <option value="active">활성</option>
              <option value="inactive">휴면</option>
              <option value="expired">만료</option>
            </select>
          </div>
        </div>

        <div>
          <label className="label">메모</label>
          <textarea value={form.memo} onChange={e => set('memo', e.target.value)} className="input min-h-[80px] resize-none" placeholder="특이사항, 주의사항 등..." rows={3} />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn-secondary">취소</button>
          <button type="submit" className="btn-primary">{member ? '수정 완료' : '등록'}</button>
        </div>
      </form>
    </>
  );
};

export default MemberForm;
